import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Chip,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Fade,
  Collapse,
  useTheme,
  Tooltip,
  Avatar,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit"; // Fixed path - using / instead of .
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import ReloadIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import SettingsIcon from "@mui/icons-material/Settings";
import FolderIcon from "@mui/icons-material/Folder";
import MemoryIcon from "@mui/icons-material/Memory";
import CancelIcon from "@mui/icons-material/Cancel"; // Added for edit cancellation
import VideocamIcon from "@mui/icons-material/Videocam"; // Added for stream section
import ExpandLess from "@mui/icons-material/ExpandLess"; // Added for section expansion controls
import ExpandMore from "@mui/icons-material/ExpandMore"; // Added for section expansion controls
import NetworkCheck from "@mui/icons-material/NetworkCheck"; // Added for Network Speed Test section
import MissedVideoCallIcon from "@mui/icons-material/MissedVideoCall"; // Added for CIDR Scan section
import TerminalIcon from "@mui/icons-material/Terminal"; // Added for Terminal section
import LanIcon from "@mui/icons-material/Lan"; // Added for Tunnel section
import AssessmentIcon from "@mui/icons-material/Assessment"; // Added for System Report section
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate"; // Added for OTA Update section

import {
  getClientDetails,
  setClientName,
  updateClient,
  addOrUpdatePath,
  deletePath,
  triggerReload,
  triggerUpdate,
  getUpdateStatus,
  getClientPaths,
} from "./api";
import { getStatusColor, formatDate } from "./utils";
import SpeedTestPanel from "./SpeedTestPanel";
import SystemInfoPanel from "./components/SystemInfoPanel";
import SystemReportPanel from "./SystemReportPanel"; // Import SystemReportPanel
import CIDRScanPanel from "./CIDRScanPanel"; // Import CIDRScanPanel
import ONVIFDiscoveryPanel from "./ONVIFDiscoveryPanel"; // Import ONVIFDiscoveryPanel
import TerminalPanel from "./TerminalPanel";
import TunnelPanel from "./TunnelPanel";
import { useAuth } from "./AuthContext";

// Status Badge Component
const StatusBadge = ({ status }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        display: "inline-block",
        bgcolor: status === "Online" ? "success.main" : "grey.500",
        boxShadow:
          status === "Online" ? "0 0 0 2px rgba(76, 175, 80, 0.2)" : "none",
        animation: status === "Online" ? "pulse 2s infinite" : "none",
        "@keyframes pulse": {
          "0%": { boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.4)" },
          "70%": { boxShadow: "0 0 0 6px rgba(76, 175, 80, 0)" },
          "100%": { boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)" },
        },
      }}
    />
    <Typography
      component="span"
      sx={{
        fontWeight: status === "Online" ? 500 : 400,
        color: status === "Online" ? "success.dark" : "text.secondary",
      }}
    >
      {status}
    </Typography>
  </Box>
);

export default function ClientDetails({
  apiKey,
  clientId,
  onClose,
  onActionSuccess,
}) {
  const theme = useTheme();
  const { isAdmin, isDev, isPartner, currentUser } = useAuth();
  
  // Helper function to check if user has access to modify this client
  const canModifyClient = useCallback((clientDetails) => {
    // Admin and Dev can modify any client
    if (isAdmin || isDev) return true;
    
    // Partner users can only modify clients belonging to their partners
    if (isPartner && currentUser?.partnerIds && clientDetails?.partnerId) {
      return currentUser.partnerIds.includes(clientDetails.partnerId);
    }
    
    return false;
  }, [isAdmin, isDev, isPartner, currentUser]);
  
  const canModify = Boolean(isAdmin || isDev || isPartner);
  const [details, setDetails] = useState(null);
  const [canModifyThisClient, setCanModifyThisClient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // For action buttons

  // Form states
  const [newName, setNewName] = useState("");
  const [newCompanyID, setNewCompanyID] = useState("");
  const [newLocationID, setNewLocationID] = useState("");
  const [newDeploymentType, setNewDeploymentType] = useState("");
  const [newPathName, setNewPathName] = useState("");
  const [newPathRun, setNewPathRun] = useState("");
  const [newPathRestart, setNewPathRestart] = useState("yes"); // Default 'no'
  const [editMode, setEditMode] = useState(false); // Track if we're editing an existing path
  const [originalPathName, setOriginalPathName] = useState(""); // Store original path name for edit mode

  // Stream management states
  const [inputRtspUrl, setInputRtspUrl] = useState("");
  const [outputRtspUrl, setOutputRtspUrl] = useState("");

  // Path management section
  const [paths, setPaths] = useState({});
  const [pathsLoading, setPathsLoading] = useState(false);
  const [newPathConfig, setNewPathConfig] = useState({
    runOnInit: "",
    runOnInitRestart: "no",
  });

  // Dialog states
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [confirmReloadDialogOpen, setConfirmReloadDialogOpen] = useState(false);
  const [pathToDelete, setPathToDelete] = useState("");

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // 'success', 'error', 'warning', 'info'

  // OTA Update states
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null); // Real-time update progress from polling

  // Section Expanded States
  const [sectionsExpanded, setSectionsExpanded] = useState({
    info: true,
    name: false,
    paths: false,
    streams: false,
    report: false, // Add System Report section
    speedTest: false, // Add this line for the speed test section
    cidrScan: false, // Add this line for the CIDR scan section
    onvifDiscovery: false, // Add this line for the ONVIF Discovery section
    terminal: false, // Add Terminal section
    tunnel: false, // Add Tunnel section
    update: false, // Add OTA Update section
    actions: true,
  });

  const toggleSection = (section) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const fetchDetails = useCallback(async () => {
    if (!apiKey || !clientId) return;
    setLoading(true);
    setError("");
    setActionError(""); // Clear previous action errors
    try {
      const data = await getClientDetails(apiKey, clientId);
      // Ensure clientID is set correctly for internal use
      data.clientID = data.id || clientId;
      setDetails(data);
      setCanModifyThisClient(canModifyClient(data));
      setNewName(data?.name || ""); // Pre-fill name field
      setNewCompanyID(data?.company?.id || ""); // Pre-fill company ID field
      setNewLocationID(data?.location?.id || ""); // Pre-fill location ID field
      setNewDeploymentType(data?.deploymentType || ""); // Pre-fill deployment type
    } catch (err) {
      console.error("Failed to fetch client details:", err);
      const errorMsg =
        err.data?.error || err.message || "Failed to fetch client details.";
      setError(`Error: ${errorMsg} (Status: ${err.status || "N/A"})`);
      setDetails(null); // Clear details on error
    } finally {
      setLoading(false);
    }
  }, [apiKey, clientId]);

  const fetchPaths = useCallback(async () => {
    if (!apiKey || !clientId) return;

    setPathsLoading(true);
    try {
      const data = await getClientPaths(apiKey, clientId);
      setPaths(data || {});
    } catch (err) {
      console.error("Failed to fetch client paths:", err);
      setActionError(`Failed to fetch paths: ${err.message}`);
    } finally {
      setPathsLoading(false);
    }
  }, [apiKey, clientId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]); // Fetch when clientId/apiKey changes

  useEffect(() => {
    if (details) {
      fetchPaths();
    }
  }, [fetchPaths, details]);

  // Stream update status via SSE when update is in progress
  useEffect(() => {
    if (!details?.isUpdating && !isUpdating) {
      setUpdateStatus(null);
      return;
    }

    let eventSource = null;
    let pollInterval = null;
    let isMounted = true;

    // Try SSE first
    const connectSSE = () => {
      try {
        // EventSource doesn't support custom headers, so we pass token as query param
        const url = `https://api1.masternode.wobot.ai/api/clients/${clientId}/update-status-stream?token=${encodeURIComponent(apiKey)}`;
        eventSource = new EventSource(url);

        eventSource.onopen = () => {
          console.log("[SSE] Connected to update status stream");
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (isMounted) {
              setUpdateStatus(data);
              
              // If update completed or failed, close SSE and refresh
              if (!data.isUpdating) {
                setIsUpdating(false);
                eventSource.close();
                setTimeout(() => {
                  if (isMounted) fetchDetails();
                }, 1000);
              }
            }
          } catch (err) {
            console.error("[SSE] Failed to parse message:", err);
          }
        };

        eventSource.onerror = (error) => {
          console.error("[SSE] Connection error:", error);
          eventSource.close();
          
          // Fallback to polling if SSE fails
          if (isMounted && !pollInterval) {
            console.log("[SSE] Falling back to polling");
            pollInterval = setInterval(async () => {
              try {
                const status = await getUpdateStatus(apiKey, clientId);
                if (isMounted) {
                  setUpdateStatus(status);
                  
                  if (!status.isUpdating) {
                    setIsUpdating(false);
                    clearInterval(pollInterval);
                    setTimeout(() => {
                      if (isMounted) fetchDetails();
                    }, 1000);
                  }
                }
              } catch (err) {
                console.error("[Polling] Failed to fetch update status:", err);
              }
            }, 2000);
          }
        };
      } catch (err) {
        console.error("[SSE] Failed to create EventSource:", err);
        // Fallback to polling immediately
        if (isMounted && !pollInterval) {
          pollInterval = setInterval(async () => {
            try {
              const status = await getUpdateStatus(apiKey, clientId);
              if (isMounted) {
                setUpdateStatus(status);
                
                if (!status.isUpdating) {
                  setIsUpdating(false);
                  clearInterval(pollInterval);
                  setTimeout(() => {
                    if (isMounted) fetchDetails();
                  }, 1000);
                }
              }
            } catch (err) {
              console.error("[Polling] Failed to fetch update status:", err);
            }
          }, 2000);
        }
      }
    };

    connectSSE();

    // Cleanup on unmount or when update completes
    return () => {
      isMounted = false;
      if (eventSource) {
        eventSource.close();
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [details?.isUpdating, isUpdating, apiKey, clientId, fetchDetails]);

  // --- Action Handlers ---

  const handleShowSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleTriggerUpdate = async () => {
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to update this client.",
        "warning"
      );
      return;
    }

    setIsUpdating(true);
    setActionError("");
    try {
      const response = await triggerUpdate(apiKey, clientId);
      handleShowSnackbar(
        `Update initiated: ${response.currentVersion} → ${response.targetVersion}`,
        "success"
      );
    } catch (err) {
      const errorMsg = err.message || "Failed to trigger update";
      setActionError(errorMsg);
      handleShowSnackbar(errorMsg, "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNameSubmit = async (event) => {
    event.preventDefault();
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    if (!details) return;
    setIsSubmitting(true);
    setActionError("");
    try {
      // Use updateClient API to update name, company, location, and deployment type
      // This updates the database directly, doesn't require client to be online
      const updatePayload = {
        name: newName,
        company: { id: newCompanyID, name: "" },
        location: { id: newLocationID, name: "" },
      };
      
      // Only include deploymentType if it's not empty string (for auto-detect)
      // or if it's explicitly set to a value
      if (newDeploymentType !== undefined) {
        updatePayload.deploymentType = newDeploymentType;
      }
      
      await updateClient(apiKey, clientId, updatePayload);
      handleShowSnackbar(`Client details updated successfully!`, "success");
      // Refresh details to show the change reflected back from server potentially
      fetchDetails();
      if (onActionSuccess) onActionSuccess(`Client details updated`); // Notify parent (e.g., App to refresh list)
    } catch (err) {
      const errorMsg =
        err.data?.error || err.message || "Failed to update client details.";
      handleShowSnackbar(`Error updating client details: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddUpdatePath = async (event) => {
    event.preventDefault();
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    if (!details || details.status !== "Online" || !newPathName) return;
    setIsSubmitting(true);
    setActionError("");
    try {
      const config = {
        runOnInit: newPathRun,
        runOnInitRestart: newPathRestart,
      };
      await addOrUpdatePath(apiKey, clientId, newPathName, config);

      const actionType = editMode ? "updated" : "added";
      handleShowSnackbar(`Path "${newPathName}" ${actionType}.`, "success");

      // Clear form and reset edit mode
      resetPathForm();
      fetchDetails();
    } catch (err) {
      const errorMsg =
        err.data?.error || err.message || "Failed to add/update path.";
      handleShowSnackbar(`Error saving path: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddOrUpdatePath = async () => {
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    if (!newPathName.trim()) return;

    setIsSubmitting(true);
    try {
      await addOrUpdatePath(apiKey, clientId, newPathName, newPathConfig);
      handleShowSnackbar(
        `Path ${newPathName} added/updated successfully`,
        "success",
      );
      // Clear form
      setNewPathName("");
      setNewPathConfig({ runOnInit: "", runOnInitRestart: "no" });
      // Refresh paths
      fetchPaths();
    } catch (err) {
      console.error(`Failed to add/update path:`, err);
      handleShowSnackbar(`Failed to add/update path: ${err.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteConfirm = (pName) => {
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    setPathToDelete(pName);
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    if (!details || details.status !== "Online" || !pathToDelete) return;
    setConfirmDeleteDialogOpen(false);
    setIsSubmitting(true); // Use submitting state for delete button too
    setActionError("");
    try {
      await deletePath(apiKey, clientId, pathToDelete);
      handleShowSnackbar(
        `Path "${pathToDelete}" delete command sent.`,
        "success",
      );
      setPathToDelete("");
      fetchDetails(); // Refresh details
    } catch (err) {
      const errorMsg =
        err.data?.error || err.message || "Failed to delete path.";
      handleShowSnackbar(`Error deleting path: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePath = async (pathName) => {
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    if (!window.confirm(`Are you sure you want to delete path "${pathName}"?`))
      return;

    setIsSubmitting(true);
    try {
      await deletePath(apiKey, clientId, pathName);
      handleShowSnackbar(`Path ${pathName} deleted successfully`, "success");
      // Refresh paths
      fetchPaths();
    } catch (err) {
      console.error(`Failed to delete path:`, err);
      handleShowSnackbar(`Failed to delete path: ${err.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReloadConfirm = () => {
    setConfirmReloadDialogOpen(true);
  };

  const handleConfirmReload = async () => {
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    if (!details || details.status !== "Online") return;
    setConfirmReloadDialogOpen(false);
    setIsSubmitting(true);
    setActionError("");
    try {
      await triggerReload(apiKey, clientId);
      handleShowSnackbar(
        `Reload command sent to client ${clientId}.`,
        "success",
      );
      // No immediate state change needed here, server handles it
    } catch (err) {
      const errorMsg =
        err.data?.error || err.message || "Failed to send reload command.";
      handleShowSnackbar(`Error sending reload: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // New function to handle edit button click
  const handleEditPath = (pathName, config) => {
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    setEditMode(true);
    setOriginalPathName(pathName);
    setNewPathName(pathName);
    setNewPathRun(config.runOnInit || "");
    setNewPathRestart(config.runOnInitRestart || "no");

    // Scroll to the form
    setTimeout(() => {
      const formElement = document.getElementById("path-edit-form");
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Function to reset the path form
  const resetPathForm = () => {
    setEditMode(false);
    setOriginalPathName("");
    setNewPathName("");
    setNewPathRun("");
    setNewPathRestart("yes");
  };

  // Stream Management handler
  const handleAddStream = async (event) => {
    event.preventDefault();
    if (!canModifyThisClient) {
      handleShowSnackbar(
        "You don't have permission to modify this client.",
        "warning",
      );
      return;
    }
    if (
      !details ||
      details.status !== "Online" ||
      !inputRtspUrl ||
      !outputRtspUrl
    )
      return;

    setIsSubmitting(true);
    setActionError("");

    try {
      // Format the ffmpeg command with the input and output URLs
      const ffmpegCommand = `ffmpeg -re -v error -rtsp_transport tcp -stimeout 10000000 -i "${inputRtspUrl}" -codec:v copy -rtsp_transport tcp -stimeout 10000000 -an -f rtsp -muxdelay 0.1 "rtsp://proxyrtsp-publish.wobot.ai:8554/${outputRtspUrl}"`;

      // Use the output URL as the path name
      const pathName = outputRtspUrl;

      // Configure the path with the ffmpeg command and restart option
      const config = {
        runOnInit: ffmpegCommand,
        runOnInitRestart: "yes",
      };

      await addOrUpdatePath(apiKey, clientId, pathName, config);

      handleShowSnackbar(`Stream "${pathName}" added successfully.`, "success");

      // Clear form
      setInputRtspUrl("");
      setOutputRtspUrl("");

      // Refresh details
      fetchDetails();
    } catch (err) {
      const errorMsg =
        err.data?.error || err.message || "Failed to add stream.";
      handleShowSnackbar(`Error adding stream: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add wsBaseUrl detection (reuse from SpeedTestPanel or set default)
  const wsBaseUrl = window.location.origin.replace(/^http/, "ws");

  // --- Render Logic ---

  if (loading)
    return (
      <Card sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Card>
    );

  if (error)
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );

  if (!details) return null;

  const isOnline = details.status === "Online";

  return (
    <Box>
      {/* Maintenance Mode Banner - Show when update is in progress */}
      {(details?.isUpdating || updateStatus?.isUpdating) && (
        <Alert 
          severity="warning" 
          icon={<SystemUpdateIcon />}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Agent Update in Progress
            </Typography>
            <Typography variant="body2">
              {updateStatus?.updateProgress?.message || "Updating agent..."}
            </Typography>
            {updateStatus?.updateProgress?.status && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Status: {updateStatus.updateProgress.status}
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      <Card elevation={2} sx={{ overflow: "hidden" }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: isOnline ? "success.main" : "grey.500" }}>
              <MemoryIcon />
            </Avatar>
          }
          title={
            <Typography variant="h6">
              {details.name || "Unnamed Client"}
            </Typography>
          }
          subheader={
            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, flexWrap: "wrap", gap: 1 }}>
              <Box
                component="code"
                sx={{
                  fontSize: "0.75rem",
                  p: 0.5,
                  bgcolor: "grey.100",
                  borderRadius: "4px",
                  color: "grey.800",
                }}
              >
                {details.id || details.clientID || clientId}
              </Box>
              <StatusBadge status={details.status} />
              <Chip
                label={details.deploymentType || "Unknown"}
                size="small"
                color={
                  details.deploymentType === "On-Prem"
                    ? "success"
                    : details.deploymentType === "Cloud"
                    ? "info"
                    : "default"
                }
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              {isPartner && !canModifyThisClient && (
                <Chip
                  size="small"
                  label="Read-only (Different Partner)"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: "20px" }}
                />
              )}
            </Box>
          }
          action={
            onClose && (
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )
          }
        />
        <Divider />
        <CardContent sx={{ pt: 0 }}>
          <Stack spacing={2}>
            {/* --- Insert System Info Panel Here --- */}
            <Box sx={{ p: 2 }}>
              <SystemInfoPanel systemInfo={details.systemInfo} />
            </Box>

            {/* OTA Update Section */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("update")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <SystemUpdateIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Agent Update
                  </Typography>
                  {sectionsExpanded.update ? <ExpandLess sx={{ ml: "auto" }} /> : <ExpandMore sx={{ ml: "auto" }} />}
                </Box>
                <Collapse in={sectionsExpanded.update}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Current Version
                          </Typography>
                          <Typography variant="h6">
                            {details.agentVersion || "Unknown"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Latest Version
                          </Typography>
                          <Typography variant="h6">
                            {details.latestVersion || "Unknown"}
                          </Typography>
                        </Box>
                        <Box>
                          {details.updateAvailable ? (
                            <Chip
                              label="Update Available"
                              color="warning"
                              size="small"
                              icon={<SystemUpdateIcon />}
                            />
                          ) : (
                            <Chip
                              label="Up to Date"
                              color="success"
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                      <Divider />
                      
                      {/* Update Progress Display */}
                      {(isUpdating || updateStatus?.isUpdating) && updateStatus?.updateProgress && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Update Progress
                          </Typography>
                          <Typography variant="body1" fontWeight={500} gutterBottom>
                            {updateStatus.updateProgress.message || "In progress..."}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Status: {updateStatus.updateProgress.status}
                          </Typography>
                          <Box sx={{ mt: 1, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                            <Typography variant="caption" component="div" color="text.secondary">
                              Last updated: {updateStatus.updateProgress.updatedAt ? new Date(updateStatus.updateProgress.updatedAt).toLocaleTimeString() : "Unknown"}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SystemUpdateIcon />}
                        onClick={handleTriggerUpdate}
                        disabled={isUpdating || updateStatus?.isUpdating || !isOnline || !details.updateAvailable}
                        fullWidth
                      >
                        {isUpdating || updateStatus?.isUpdating ? "Updating..." : "Update Agent"}
                      </Button>
                      {!isOnline && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Agent must be online to perform updates
                        </Alert>
                      )}
                      {!details.updateAvailable && isOnline && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Agent is running the latest version
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* System Report Panel */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("report")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    mb: 0,
                  }}
                >
                  <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    System Report
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.report}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <SystemReportPanel
                      clientDetails={details}
                      onError={(err) => handleShowSnackbar(err, "error")}
                      onRefresh={fetchDetails}
                      apiKey={apiKey}
                    />
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Set Name Form */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("name")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <EditIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Update Device Details
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.name}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Box
                      component="form"
                      onSubmit={handleNameSubmit}
                      noValidate
                    >
                      <Stack spacing={2}>
                        <TextField
                          size="small"
                          required
                          fullWidth
                          id="clientName"
                          label="Client Name"
                          name="clientName"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <TextField
                          size="small"
                          fullWidth
                          id="clientCompanyID"
                          label="Company ID"
                          name="clientCompanyID"
                          value={newCompanyID}
                          onChange={(e) => setNewCompanyID(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <TextField
                          size="small"
                          fullWidth
                          id="clientLocationID"
                          label="Location ID"
                          name="clientLocationID"
                          value={newLocationID}
                          onChange={(e) => setNewLocationID(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <FormControl fullWidth size="small">
                          <InputLabel id="deployment-type-label">
                            Deployment Type
                          </InputLabel>
                          <Select
                            labelId="deployment-type-label"
                            id="deploymentType"
                            value={newDeploymentType}
                            label="Deployment Type"
                            onChange={(e) => setNewDeploymentType(e.target.value)}
                            disabled={isSubmitting}
                          >
                            <MenuItem value="">
                              <em>Auto-detect</em>
                            </MenuItem>
                            <MenuItem value="On-Prem">On-Prem</MenuItem>
                            <MenuItem value="Cloud">Cloud</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={
                            isSubmitting ||
                            (newName === details.name &&
                              newCompanyID === (details.company?.id || "") &&
                              newLocationID === (details.location?.id || "") &&
                              newDeploymentType === (details.deploymentType || ""))
                          }
                          startIcon={<SaveIcon />}
                          fullWidth
                        >
                          {isSubmitting ? "Saving..." : "Save Details"}
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Paths Table and Add Form */}
            <Box>
              <Box
                onClick={() => toggleSection("paths")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  p: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <FolderIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight={500}>
                  Path Configurations
                </Typography>
              </Box>
              <Collapse in={sectionsExpanded.paths}>
                <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                  {isPartner && !canModifyThisClient && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You don't have permission to modify path configurations for this client as it belongs to a different partner.
                    </Alert>
                  )}
                  <TableContainer
                    sx={{ mb: 3, maxHeight: 300, overflow: "auto" }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Path Name</TableCell>
                          <TableCell>Run On Init Command</TableCell>
                          <TableCell>Restart on Error?</TableCell>
                          {canModifyThisClient && (
                            <TableCell align="right">Actions</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(details.paths || {}).length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={canModifyThisClient ? 4 : 3}
                              align="center"
                            >
                              <Box sx={{ py: 3 }}>
                                <Typography color="textSecondary">
                                  No paths configured.
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                        {Object.entries(details.paths || {}).map(
                          ([pathName, config]) => (
                            <TableRow
                              key={pathName}
                              hover
                              sx={{
                                bgcolor:
                                  editMode && originalPathName === pathName
                                    ? (theme) => theme.palette.action.selected
                                    : "inherit",
                              }}
                            >
                              <TableCell>
                                <Box
                                  sx={{
                                    fontSize: "0.75rem",
                                    p: 0.5,
                                    bgcolor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : theme.palette.code?.background ||
                                          "rgba(0, 0, 0, 0.05)",
                                    borderRadius: "4px",
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(255, 255, 255, 0.85)"
                                        : theme.palette.code?.color ||
                                          "rgba(0, 0, 0, 0.87)",
                                  }}
                                >
                                  {pathName}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Tooltip
                                  title={
                                    config.runOnInit || "No command specified"
                                  }
                                  arrow
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: "200px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {config.runOnInit || "-"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={config.runOnInitRestart || "no"}
                                  color={
                                    config.runOnInitRestart === "yes"
                                      ? "primary"
                                      : "default"
                                  }
                                  variant={
                                    config.runOnInitRestart === "yes"
                                      ? "filled"
                                      : "outlined"
                                  }
                                />
                              </TableCell>
                              {canModifyThisClient && (
                                <TableCell align="right">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <Tooltip title="Edit path">
                                      <IconButton
                                        aria-label="edit path"
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          handleEditPath(pathName, config)
                                        }
                                        disabled={!isOnline || isSubmitting}
                                        sx={{ mr: 1 }}
                                      >
                                        <EditIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete path">
                                      <IconButton
                                        aria-label="delete path"
                                        color="error"
                                        size="small"
                                        onClick={() =>
                                          openDeleteConfirm(pathName)
                                        }
                                        disabled={!isOnline || isSubmitting}
                                      >
                                        <DeleteIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              )}
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {canModifyThisClient && (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(30, 30, 30, 0.9)"
                            : "grey.50",
                        borderRadius: 1,
                        border: editMode ? "1px solid" : "none",
                        borderColor: "primary.main",
                        transition: "all 0.3s ease",
                      }}
                      id="path-edit-form"
                    >
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        fontWeight={500}
                      >
                        {editMode ? "Edit Path" : "Add New Path"}
                      </Typography>
                      <Box
                        component="form"
                        onSubmit={handleAddUpdatePath}
                        noValidate
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              required
                              fullWidth
                              name="newPathName"
                              label="Path Name (e.g., myApp)"
                              value={newPathName}
                              onChange={(e) => setNewPathName(e.target.value)}
                              disabled={!isOnline || isSubmitting}
                              InputProps={{
                                readOnly: editMode, // In edit mode, don't allow changing the path name
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              size="small"
                              fullWidth
                              name="newPathRun"
                              label="Run On Init Command"
                              value={newPathRun}
                              onChange={(e) => setNewPathRun(e.target.value)}
                              disabled={!isOnline || isSubmitting}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              name="newPathRestart"
                              label="Restart on Error?"
                              value={newPathRestart}
                              onChange={(e) =>
                                setNewPathRestart(e.target.value)
                              }
                              disabled={!isOnline || isSubmitting}
                              SelectProps={{ native: true }}
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </TextField>
                          </Grid>
                          <Grid item xs={editMode ? 6 : 12}>
                            <Button
                              type="submit"
                              variant="contained"
                              startIcon={editMode ? <SaveIcon /> : <AddIcon />}
                              disabled={
                                !isOnline || isSubmitting || !newPathName
                              }
                              fullWidth
                              color={editMode ? "primary" : "primary"}
                            >
                              {isSubmitting
                                ? "Saving..."
                                : editMode
                                  ? "Update Path"
                                  : "Add Path"}
                            </Button>
                          </Grid>
                          {editMode && (
                            <Grid item xs={6}>
                              <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                disabled={isSubmitting}
                                fullWidth
                                onClick={resetPathForm}
                                color="secondary"
                              >
                                Cancel
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Collapse>
            </Box>

            {/* Stream Management */}
            {canModifyThisClient && (
              <Box>
                <Box
                  onClick={() => toggleSection("streams")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <VideocamIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Stream Management
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.streams}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Box component="form" onSubmit={handleAddStream} noValidate>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            required
                            fullWidth
                            name="inputRtspUrl"
                            label="Input RTSP URL"
                            value={inputRtspUrl}
                            onChange={(e) => setInputRtspUrl(e.target.value)}
                            disabled={!isOnline || isSubmitting}
                            placeholder="rtsp://example.com/stream"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            required
                            fullWidth
                            name="outputRtspUrl"
                            label="Output RTSP URL"
                            value={outputRtspUrl}
                            onChange={(e) => setOutputRtspUrl(e.target.value)}
                            disabled={!isOnline || isSubmitting}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  rtsp://proxyrtsp-publish.wobot.ai:8554/
                                </InputAdornment>
                              ),
                            }}
                            placeholder="mystream"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            disabled={
                              !isOnline ||
                              isSubmitting ||
                              !inputRtspUrl ||
                              !outputRtspUrl
                            }
                            fullWidth
                          >
                            {isSubmitting ? "Adding Stream..." : "Add Stream"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Reload Action */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("actions")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <SettingsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Client Actions
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.actions}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ReloadIcon />}
                        onClick={openReloadConfirm}
                        disabled={!isOnline || isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Trigger Client Reload"}
                      </Button>

                      <Typography variant="caption" color="text.secondary">
                        Forces client to reload its configuration.
                      </Typography>
                    </Box>
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Add SpeedTest Section after Reload Action */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("speedTest")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    mb: 0,
                  }}
                >
                  <NetworkCheck color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Network Speed Test
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.speedTest}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <SpeedTestPanel
                      apiKey={apiKey}
                      clientId={clientId}
                      clientName={details?.name}
                      onError={(err) => handleShowSnackbar(err, "error")}
                    />
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Add CIDR Scan Section after SpeedTest */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("cidrScan")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    mb: 0,
                  }}
                >
                  <MissedVideoCallIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Scan Camera and NVR
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.cidrScan}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <CIDRScanPanel
                      apiKey={apiKey}
                      clientId={clientId}
                      clientName={details?.name}
                      onError={(err) => handleShowSnackbar(err, "error")}
                    />
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Add ONVIF Discovery Section after CIDR Scan */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("onvifDiscovery")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    mb: 0,
                  }}
                >
                  <VideocamIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    ONVIF Discovery
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.onvifDiscovery}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <ONVIFDiscoveryPanel
                      apiKey={apiKey}
                      clientId={clientId}
                      clientName={details?.name}
                      onError={(err) => handleShowSnackbar(err, "error")}
                    />
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Terminal Panel */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("terminal")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    mb: 0,
                  }}
                >
                  <TerminalIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Remote Terminal
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.terminal}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <TerminalPanel
                      apiKey={apiKey}
                      clientId={clientId}
                      clientName={details?.name}
                      onError={(err) => handleShowSnackbar(err, "error")}
                    />
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Tunnel Panel */}
            {canModify && (
              <Box>
                <Box
                  onClick={() => toggleSection("tunnel")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    mb: 0,
                  }}
                >
                  <LanIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={500}>
                    Port Forwarding Tunnel
                  </Typography>
                </Box>
                <Collapse in={sectionsExpanded.tunnel}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <TunnelPanel
                      apiKey={apiKey}
                      clientId={clientId}
                      clientName={details?.name}
                      onError={(err) => handleShowSnackbar(err, "error")}
                    />
                  </Paper>
                </Collapse>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={() => setConfirmDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the path configuration for:{" "}
            <code>{pathToDelete}</code>? This will send a command to the client.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setConfirmDeleteDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            disabled={isSubmitting}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reload Confirmation Dialog */}
      <Dialog
        open={confirmReloadDialogOpen}
        onClose={() => setConfirmReloadDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle id="alert-dialog-title">Confirm Reload</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to trigger a reload for client{" "}
            <code>{clientId}</code>? This will force the client to reload its
            configuration from disk.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setConfirmReloadDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReload}
            color="secondary"
            autoFocus
            disabled={isSubmitting}
            variant="contained"
            startIcon={<ReloadIcon />}
          >
            {isSubmitting ? "Sending..." : "Reload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000} // 6 seconds
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
